import type { RedirectRequest } from '@azure/msal-browser';
import { addBreadcrumb, captureException, captureMessage, logger, setUser } from '@sentry/react';
import { type Deployment, parseDeployment } from '@workspace/shared';

import type { DialogToParentMessage } from '@/api/types/authMessages';

import { resolveApiHost } from './deviceToken';
import { getMsalInstance } from './msal';
import { backendApiRequest, loginRequest } from './msalConfig';

/**
 * Runs INSIDE the Connect Mailbox dialog opened from the taskpane (see
 * `msalConnectDialog.ts`). The dialog performs MSAL's REDIRECT flow in its own
 * top-level window, so no code here (or in the taskpane) ever polls a window
 * handle — which is what makes this immune to Cross-Origin-Opener-Policy
 * headers on federated identity providers.
 *
 * Flow:
 *  - First load (`?msalConnect=1`, login hint in the URL fragment): stamp a
 *    sessionStorage marker and `loginRedirect` away to Microsoft login.
 *  - Return load (marker + auth response in the fragment): let MSAL redeem the
 *    response (`handleRedirectPromise`), then relay the identity token to the
 *    taskpane via `messageParent`. The token rides the Office message channel;
 *    the auth code only ever exists in the URL fragment, which never leaves
 *    the browser.
 *
 * The marker lives in the dialog window's OWN sessionStorage: it survives the
 * round-trip to the identity provider (same top-level window) but is invisible
 * to the taskpane and dies with the dialog, so a normal taskpane boot can
 * never be mistaken for a dialog return leg.
 */

export const MSAL_CONNECT_MARKER_KEY = 'msalConnectDialog:active';

/** True when the fragment carries an OAuth authorization response. */
export const hasAuthResponseHash = (hash: string): boolean => /[#&](code|error)=/.test(hash);

/**
 * True when this window is the Connect Mailbox dialog surface — either the
 * first load (query flag) or the return leg from Microsoft login (marker set
 * by the first load). Used by main.tsx to route this window away from the
 * full app boot.
 */
export function isMsalConnectSurface(searchParams: URLSearchParams): boolean {
  return (
    searchParams.get('msalConnect') === '1' ||
    sessionStorage.getItem(MSAL_CONNECT_MARKER_KEY) !== null
  );
}

/** Relays a result to the taskpane; never throws (there is no one to catch). */
async function relayToTaskpane(message: DialogToParentMessage): Promise<void> {
  try {
    await Office.onReady();
    Office.context.ui.messageParent(JSON.stringify(message));
  } catch (error) {
    // A failed relay is invisible to the taskpane, which then waits out its
    // full 5-minute timeout — always worth an event of its own.
    // oxlint-disable-next-line no-console
    console.error('Failed to relay MSAL dialog result to the taskpane:', error);
    captureException(error, {
      tags: { messageType: message.type, stage: 'relay', surface: 'msal-connect-dialog' },
    });
  }
}

/** The user backed out at the provider (declined consent / hit cancel). */
const isUserDeclinedError = (error: unknown): boolean =>
  (error as { errorCode?: string } | null)?.errorCode === 'access_denied';

/** What the taskpane handed us, round-tripped through the identity provider. */
interface DialogHandoff {
  deployment: Deployment;
  ticket: string;
}

/**
 * The handoff rides MSAL's `state` rather than this window's storage: MSAL
 * round-trips it through every provider hop and hands it back from
 * handleRedirectPromise, and its own copy is cookie-backed
 * (storeAuthStateInCookie), so it survives contexts where sessionStorage does
 * not.
 */
function parseHandoffState(state: string | undefined): DialogHandoff | null {
  if (!state) {
    return null;
  }
  try {
    const parsed = JSON.parse(state) as { deployment?: unknown; ticket?: unknown };
    // parseDeployment, not a string check: this value originates in a URL
    // fragment anyone can craft, and it decides where a Microsoft identity
    // token gets posted. Only a name from the known set is accepted, and it is
    // resolved to one of our own hosts below.
    const deployment = parseDeployment(parsed.deployment);
    if (!deployment || typeof parsed.ticket !== 'string') {
      return null;
    }
    return { deployment, ticket: parsed.ticket };
  } catch {
    return null;
  }
}

/**
 * Completes the mailbox link from this window, over plain HTTP.
 *
 * This is the path that survives a severed window handle: by the time the user
 * finishes at a federated provider the taskpane may have been told this dialog
 * closed, so `messageParent` is no longer guaranteed to reach anyone. The
 * ticket lets us finish the job without it.
 *
 * `keepalive` so the request still completes if the taskpane closes this window
 * the moment the relay below lands.
 */
async function completeLinkWithTicket(
  handoff: DialogHandoff,
  identityToken: string,
): Promise<boolean> {
  try {
    // Resolved from the deployment name, so the destination is always one of
    // our hosts no matter what the fragment said.
    const host = resolveApiHost(handoff.deployment);
    const response = await fetch(`${host}/auth/connect-mailbox-via-ticket`, {
      body: JSON.stringify({ identityToken, ticket: handoff.ticket }),
      headers: { 'content-type': 'application/json' },
      keepalive: true,
      method: 'POST',
    });

    if (!response.ok) {
      logger.warn('Connect mailbox dialog: ticket completion rejected', {
        status: response.status,
      });
      return false;
    }

    logger.info('Connect mailbox dialog: mailbox linked from the dialog window');
    return true;
  } catch (error) {
    // Never fatal — the relay below is still a route home on hosts where the
    // window handle survived.
    // oxlint-disable-next-line no-console
    console.error('[MsalDialogHandler] ticket completion failed:', error);
    captureException(error, {
      tags: { stage: 'ticket-completion', surface: 'msal-connect-dialog' },
    });
    return false;
  }
}

async function handleReturnLeg(hash: string): Promise<void> {
  sessionStorage.removeItem(MSAL_CONNECT_MARKER_KEY);
  addBreadcrumb({
    data: { hashHasError: /[#&]error=/.test(hash) },
    message: '[MsalDialogHandler] return leg: redeeming the auth response',
  });
  logger.info('Connect mailbox dialog: redeeming the Microsoft auth response', {
    hashHasError: /[#&]error=/.test(hash),
  });
  try {
    const pca = await getMsalInstance();
    // Pass the captured hash explicitly so nothing that boots alongside us
    // (office.js rewrites history APIs inside dialogs) can race MSAL to it.
    const result = await pca.handleRedirectPromise(hash);

    if (!result) {
      // MSAL found no interaction to complete — usually its own temp state was
      // dropped between the two legs, so the response cannot be redeemed here.
      // oxlint-disable-next-line no-console
      console.error(
        '[MsalDialogHandler] handleRedirectPromise returned no result on the return leg; MSAL had no matching interaction state for this auth response.',
      );
      captureMessage('Connect Mailbox: return leg produced no credential', {
        level: 'error',
        tags: { surface: 'msal-connect-dialog' },
      });
      await relayToTaskpane({
        error: 'Microsoft sign-in did not return a credential. Please try again.',
        type: 'auth:error',
      });
      return;
    }

    // The return leg's fragment carries the auth code, not the login hint, so
    // this is the first point where this window knows who signed in.
    if (result.account?.username) {
      setUser({ email: result.account.username, id: result.account.username });
    }

    // Finish the link here first, while we still exist. MSAL round-trips the
    // state we set on the first leg, so the ticket survives the provider hops
    // without depending on this window's storage.
    const handoff = parseHandoffState(result.state);
    if (handoff) {
      await completeLinkWithTicket(handoff, result.accessToken);
    } else {
      logger.warn('Connect mailbox dialog: no ticket on the returned state');
    }

    await relayToTaskpane({
      accessToken: result.accessToken,
      // UPN/email of the authenticated user — always populated after an
      // interactive flow, unlike Office.context.mailbox.userProfile.
      email: result.account?.username ?? null,
      type: 'msal:token',
    });
  } catch (error) {
    if (isUserDeclinedError(error)) {
      addBreadcrumb({
        message: '[MsalDialogHandler] user declined consent at the provider',
        type: 'info',
      });
      logger.warn('Connect mailbox dialog: user declined consent at the provider');
      await relayToTaskpane({ type: 'auth:cancel' });
      return;
    }
    // The taskpane only receives the summary string, so the full error (AADSTS
    // code, correlation id) has to be logged and captured here in the dialog.
    // oxlint-disable-next-line no-console
    console.error('[MsalDialogHandler] failed to redeem the Microsoft auth response:', error);
    captureException(error, {
      tags: { stage: 'return-leg', surface: 'msal-connect-dialog' },
    });
    await relayToTaskpane({
      error: error instanceof Error ? error.message : 'Microsoft sign-in failed.',
      type: 'auth:error',
    });
  }
}

async function handleFirstLeg(hash: string): Promise<void> {
  try {
    const pca = await getMsalInstance();
    // Canonical MSAL prescription: settle any leftover redirect/interaction
    // state in this window before starting a new interactive request.
    await pca.handleRedirectPromise();

    // Request the backend API token AND consent for the Graph scopes in one
    // interaction — the same request shape the popup flow used.
    const request: RedirectRequest = {
      ...backendApiRequest,
      extraScopesToConsent: loginRequest.scopes,
    };

    const fragment = new URLSearchParams(hash.slice(1));
    const loginHint = fragment.get('loginHint');
    if (loginHint) {
      request.loginHint = loginHint;
    }

    // Carry the ticket and API host through the provider hops on MSAL's state,
    // so the return leg can finish the link even if nobody is listening for
    // messageParent by then.
    const deployment = parseDeployment(fragment.get('deployment'));
    const ticket = fragment.get('ticket');
    if (deployment && ticket) {
      request.state = JSON.stringify({ deployment, ticket } satisfies DialogHandoff);
    }

    sessionStorage.setItem(MSAL_CONNECT_MARKER_KEY, '1');
    addBreadcrumb({
      data: { hasLoginHint: Boolean(request.loginHint) },
      message: '[MsalDialogHandler] first leg: redirecting to Microsoft login',
    });
    // The two legs run in the dialog window, which captures no error event on a
    // cancelled flow — these logs are the only evidence the dialog got as far
    // as Microsoft, which is what separates "blocked" from "user backed out".
    logger.info('Connect mailbox dialog: redirecting to Microsoft login');
    await pca.loginRedirect(request); // navigates away; resolves only on failure paths
  } catch (error) {
    sessionStorage.removeItem(MSAL_CONNECT_MARKER_KEY);
    // oxlint-disable-next-line no-console
    console.error('[MsalDialogHandler] could not start the Microsoft sign-in redirect:', error);
    captureException(error, {
      tags: { stage: 'first-leg', surface: 'msal-connect-dialog' },
    });
    await relayToTaskpane({
      error: error instanceof Error ? error.message : 'Could not start Microsoft sign-in.',
      type: 'auth:error',
    });
  }
}

/**
 * Entry point, called from main.tsx instead of booting the app when
 * `isMsalConnectSurface` matched.
 */
export async function handleMsalConnectDialog(): Promise<void> {
  // Capture before anything else runs — office.js nulls out
  // history.replaceState inside its dialog runtime and MSAL itself scrubs the
  // hash while processing.
  const hash = window.location.hash;
  const searchParams = new URLSearchParams(window.location.search);

  // This window never boots the React app, so App.tsx's identify effect never
  // runs here and dialog-side events would otherwise be anonymous. The login
  // hint is the mailbox we were asked to connect — enough to tie these events
  // to the same person as the taskpane's.
  const hintedMailbox = new URLSearchParams(hash.slice(1)).get('loginHint');
  if (hintedMailbox) {
    setUser({ email: hintedMailbox, id: hintedMailbox });
  }

  if (sessionStorage.getItem(MSAL_CONNECT_MARKER_KEY) !== null && hasAuthResponseHash(hash)) {
    await handleReturnLeg(hash);
    return;
  }

  if (searchParams.get('msalConnect') === '1') {
    await handleFirstLeg(hash);
    return;
  }

  // Marker without an auth response: the dialog reloaded somewhere unexpected
  // (e.g. the user navigated back). Fail explicitly rather than booting the
  // full app inside the dialog.
  sessionStorage.removeItem(MSAL_CONNECT_MARKER_KEY);
  // oxlint-disable-next-line no-console
  console.error(
    '[MsalDialogHandler] connect dialog reloaded with the marker set but no auth response in the fragment — the sign-in leg was interrupted (back-navigation or a webview reload).',
  );
  captureMessage('Connect Mailbox: dialog reloaded without an auth response', {
    level: 'warning',
    tags: { surface: 'msal-connect-dialog' },
  });
  await relayToTaskpane({
    error: 'Microsoft sign-in did not complete. Please try again.',
    type: 'auth:error',
  });
}
